import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { useActiveAccount } from '@/store/active-account/active-account.store';
import type { Location } from '@/store/marina-services/marina-service.dto';
import { getInfiniteQueryListNextParam } from '@/utils/list';

import {
  addServiceProviderLocation,
  createServiceProvider,
  deleteServiceProvider,
  deleteServiceProviderImage,
  deleteServiceProviderNote,
  getPorts,
  getServiceProviders,
  getSingleServiceProvider,
  type ServiceProviderLocationBody,
  setServerProviderLogo,
  updateServiceProvider,
  updateServiceProviderFavorite,
  updateServiceProviderLocation,
  updateServiceProviderNote,
  uploadServiceProviderImages,
} from './service-providers.api';
import {
  type CreateUpdateServiceProvidersFormValues,
  type PortsFilters,
  ServiceProviderCreatedBy,
  type ServiceProvidersFilters,
} from './service-providers.dto';

export const useCreateServiceProvider = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      vesselId,
      values,
      logo,
      images,
    }: {
      vesselId: string;
      values: CreateUpdateServiceProvidersFormValues;
      logo: null | File;
      images?: File[];
    }) => {
      const res = await createServiceProvider(vesselId, values);
      if (logo) {
        // Do NOT re-scope X-Service-Id to the new provider here. PATCH /logo is gated by
        // SPCreatorMiddleware (creator_id == user_id || creator_id == service_id): a catalogue
        // entry's creator_id is the *vessel*, so the header has to stay on the vessel or the
        // PATCH 403s — and the failed mutation leaves the poisoned id in localStorage. An SP
        // account's creator_id is the person, so it matches on user_id regardless.
        await setServerProviderLogo(res.id, logo);
      }
      if (images?.length) {
        await uploadServiceProviderImages(res.id, images);
      }
      return res;
    },
    onSuccess: (res) => {
      // Make the just-created provider the active account in memory. Callers only set
      // the token (setServiceId); without this the /my-services pages' selectedServiceProviderId
      // stays undefined until a full reload re-derives it, so the page renders empty.
      // updateRoleForVessel no-ops until me() adds the role, so ordering is safe.
      if (res.created_by === ServiceProviderCreatedBy.SERVICE_PROVIDER) {
        useActiveAccount.getState().setActiveAccount(res.id, 'service-provider');
      }
      queryClient.invalidateQueries({ queryKey: ['service-providers-list'] });
    },
  });
};

/**
 * `enabled: false` keeps the request back where the caller may not hold
 * `BasicServiceProvider` — the catalogue is a Command-tier right, and asking without it is
 * a 403 rather than an empty list.
 */
/** `keepPrevious` holds the last answer on screen while a changed filter loads: a search-as-you-type box. */
export const useServiceProvidersQuery = (
  limit: number,
  filters: ServiceProvidersFilters,
  { enabled = true, keepPrevious = false }: { enabled?: boolean; keepPrevious?: boolean } = {},
) => {
  const { vesselId, isFavorite, type, search, currentLat, currentLon, category, howFar } = filters;

  return useInfiniteQuery({
    enabled,
    placeholderData: keepPrevious ? keepPreviousData : undefined,
    queryKey: [
      'service-providers-list',
      vesselId,
      limit,
      isFavorite,
      type,
      search,
      currentLat,
      currentLon,
      category,
      howFar,
    ],
    initialPageParam: 1,
    queryFn: ({ pageParam }) => getServiceProviders(limit, pageParam, filters),
    getNextPageParam: getInfiniteQueryListNextParam,
  });
};

export const useSingleServiceProviderQuery = ({
  id,
  vesselId,
  latitude,
  longitude,
}: {
  id: string;
  vesselId?: string;
  latitude?: number;
  longitude?: number;
}) =>
  useQuery({
    queryKey: ['single-service-provider', vesselId, id, latitude, longitude],
    queryFn: () => getSingleServiceProvider(id, vesselId, latitude, longitude),
    enabled: !!id,
  });

/**
 * A name and a logo change when someone saves the provider form, and that save
 * invalidates every `single-service-provider` query — so between saves, the copy in hand
 * is the answer, not a reason to ask again.
 */
const ACCOUNT_PROFILE_STALE_TIME = 10 * 60_000;
/** Longer than the stale time: the switcher unmounts on close, and would drop it at 5. */
const ACCOUNT_PROFILE_GC_TIME = 30 * 60_000;

/**
 * One of the signed-in person's own provider accounts, as the header, the drawer and the
 * account switcher show it — a name and a logo. One key for all three, with no vessel in
 * it: the header asked with one and the switcher without, so the active provider was
 * fetched and cached twice. Opening the switcher reads these from cache instead of
 * sending one request per account every time.
 */
export const useAccountServiceProviderQuery = (id: string) =>
  useQuery({
    queryKey: ['single-service-provider', undefined, id, undefined, undefined],
    queryFn: () => getSingleServiceProvider(id),
    enabled: !!id,
    staleTime: ACCOUNT_PROFILE_STALE_TIME,
    gcTime: ACCOUNT_PROFILE_GC_TIME,
  });

/**
 * Everything the form collects that is *not* a column on the provider — the address, the service
 * categories, the certifications — belongs to the provider's first `service_provider_locations`
 * row, and `PUT /service-provider/:id` ignores all three. Mirror them onto the locations endpoint,
 * resending the address parts the form never renders so the wholesale row replacement keeps them.
 */
const syncServiceProviderLocation = async (
  id: string,
  values: CreateUpdateServiceProvidersFormValues,
  existing?: Location,
) => {
  const address = values.location?.address ?? existing?.location?.address ?? null;
  const body: ServiceProviderLocationBody = {
    location: {
      // `name` is the only field the backend requires, and no input is rendered for it.
      name: values.location?.name || existing?.location?.name || address || 'Location',
      address,
      latitude: values.location?.latitude ?? existing?.location?.latitude ?? 0,
      longitude: values.location?.longitude ?? existing?.location?.longitude ?? 0,
      city: existing?.location?.city ?? null,
      place_id: existing?.location?.place_id ?? null,
      state: existing?.location?.state ?? null,
      zip_code: existing?.location?.zip_code ?? null,
    },
    service_category: values.service_category ?? [],
    certifications: values.certifications ?? [],
  };

  if (existing?.id) {
    await updateServiceProviderLocation(id, existing.id, body);
  } else {
    await addServiceProviderLocation(id, body);
  }
};

export const useUpdateServiceProvider = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      vesselId,
      values,
      logo,
      images,
      deletedImageIds,
      location,
    }: {
      id: string;
      vesselId: string;
      values: CreateUpdateServiceProvidersFormValues;
      logo: null | File;
      images?: File[];
      deletedImageIds?: string[];
      /** The provider's first locations row, when it has one — the categories live on it. */
      location?: Location;
    }) => {
      const res = await updateServiceProvider(id, vesselId, values);
      await syncServiceProviderLocation(id, values, location);
      if (logo) {
        await setServerProviderLogo(id, logo);
      }
      if (images?.length) {
        await uploadServiceProviderImages(id, images);
      }
      if (deletedImageIds?.length) {
        await Promise.all(
          deletedImageIds.map((imageId) => deleteServiceProviderImage(id, imageId)),
        );
      }
      return res;
    },
    onSuccess: (_, { id, vesselId }) => {
      queryClient.invalidateQueries({ queryKey: ['service-providers-list'] });
      queryClient.invalidateQueries({ queryKey: ['single-service-provider', vesselId, id] });
    },
  });
};

export const useServiceProviderUpdateFavoriteMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, vesselId, favorite }: { id: string; vesselId: string; favorite: boolean }) =>
      updateServiceProviderFavorite(id, vesselId, favorite),
    onSuccess: (_, { vesselId, id }) => {
      queryClient.invalidateQueries({ queryKey: ['service-providers-list'] });
      queryClient.invalidateQueries({ queryKey: ['single-service-provider', vesselId, id] });
    },
  });
};

export const useServiceProviderUpdateNoteMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, vesselId, note }: { id: string; vesselId: string; note: string }) =>
      updateServiceProviderNote(id, vesselId, note),
    onSuccess: (_, { vesselId, id }) => {
      queryClient.invalidateQueries({ queryKey: ['service-providers-list'] });
      queryClient.invalidateQueries({ queryKey: ['single-service-provider', vesselId, id] });
    },
  });
};

export const useServiceProviderDeleteNoteMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, vesselId }: { id: string; vesselId: string }) =>
      deleteServiceProviderNote(id, vesselId),
    onSuccess: (_, { vesselId, id }) => {
      queryClient.invalidateQueries({ queryKey: ['service-providers-list'] });
      queryClient.invalidateQueries({ queryKey: ['single-service-provider', vesselId, id] });
    },
  });
};

export const useDeleteServiceProviderMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteServiceProvider(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-providers-list'] });
    },
  });
};

export const usePortsQuery = ({
  limit,
  page,
  filters,
}: {
  limit: number;
  page: number;
  filters: PortsFilters;
}) => {
  const { currentLat, currentLon } = filters;

  return useQuery({
    queryKey: ['ports-list', limit, page, currentLat, currentLon],
    queryFn: () => getPorts(limit, page, filters),
  });
};
